import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

export type KeyboardHeightListener = (height: number) => void;

/**
 * Subscribes to the effective on-screen keyboard height (0 when hidden), abstracting the
 * native-Capacitor vs. plain-web tracking mechanism. Returns an unsubscribe function.
 */
export function subscribeToKeyboardHeight(listener: KeyboardHeightListener): () => void {
  if (Capacitor.getPlatform() === 'web') {
    return subscribeToViewportKeyboardHeight(listener);
  }

  let latchedHeight = 0;
  const showSubscription = Keyboard.addListener('keyboardWillShow', ({ keyboardHeight }) => {
    latchedHeight = keyboardHeight > 0 ? keyboardHeight : latchedHeight;
    listener(latchedHeight);
  });
  const hideSubscription = Keyboard.addListener('keyboardWillHide', () => listener(0));

  return () => {
    void showSubscription.then(handle => handle.remove());
    void hideSubscription.then(handle => handle.remove());
  };
}

// No native Keyboard plugin on plain web — approximate visibility/height from viewport shrink.
// Height is measured against a floor baseline (the viewport height with no keyboard showing),
// not the previous sample — a keyboard opening across more than one resize event (a common
// step-wise animation) would otherwise only ever report the last increment instead of the full
// height so far.
function subscribeToViewportKeyboardHeight(listener: KeyboardHeightListener): () => void {
  const viewport = window.visualViewport;
  if (!viewport) {
    return () => undefined;
  }

  let baselineHeight = viewport.height;
  let lastWidth = viewport.width;

  const onResize = () => {
    if (viewport.width !== lastWidth) {
      // Rotation/resize rather than a keyboard transition — the new size is the new baseline.
      baselineHeight = viewport.height;
      lastWidth = viewport.width;
      listener(0);
      return;
    }
    const height = Math.max(0, baselineHeight - viewport.height);
    listener(height);
    if (height === 0) {
      baselineHeight = viewport.height;
    }
  };

  viewport.addEventListener('resize', onResize, { passive: true });
  return () => viewport.removeEventListener('resize', onResize);
}
